/**
 * 商户门户审批/通知提示音（纯函数 + WebAudio 双音提示，无音频资源文件依赖）
 *
 * canonical = web/admin/src/lib/reviewSound.ts（fleet sound-merchant 2026-09-28：
 * 跨门户口径 = 逐字复制纯函数逻辑 + 文件头钉 canonical，门户独立构建、无共享包；
 * 规则变更须先改 admin canonical，再逐字同步本文件）。
 *
 * 触发链路：`hooks/useNoticeSound.ts` 60s 轮询两条异质链（消息未读数 / 审批中心
 * APC-002 待办数），逐链独立判定，任一链计数较上次可信快照「增加」时播放提示音。规则：
 * - 仅在该链计数【可信】时比较（null = 接口失败/未加载，不可信，避免恢复时把回升误报为新增）；
 * - 仅「增加」触发，减少/不变不响；两链异质，禁合计（一增一减时合计漏报）。
 *
 * 浏览器自动播放策略：用户未与页面交互前 AudioContext 处于 suspended，resume 失败即静默跳过，
 * 不报错不阻塞（降级为无提示音）。
 *
 * 纯函数部分可被 node 直跑单测：node --experimental-strip-types src/lib/noticeSound.test.ts
 */

/** 仅「可信计数增加」返回 true（首次快照 / 不可信 / 未增加均不响）——与 canonical shouldAlertReviewIncrease 逐字同逻辑 */
export function shouldAlertNoticeIncrease(prevCount: number | null, nextCount: number | null): boolean {
  if (prevCount == null || nextCount == null) {
    return false;
  }
  return nextCount > prevCount;
}

let audioCtx: AudioContext | null = null;

/** 双音提示音（WebAudio 合成，无资源文件）；任何失败静默降级 */
export function playNoticeSound(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      return;
    }
    audioCtx ??= new Ctor();
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume().catch(() => undefined);
    }
    const t0 = audioCtx.currentTime;
    // A5 → E6 两声「叮咚」，短促不刺耳
    const tones: Array<{ freq: number; at: number }> = [
      { freq: 880, at: 0 },
      { freq: 1318.51, at: 0.16 },
    ];
    for (const { freq, at } of tones) {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + at);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.28);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(t0 + at);
      osc.stop(t0 + at + 0.32);
    }
  } catch {
    // 音频不可用（无设备/策略限制）→ 静默降级
  }
}
